import React, { useEffect, useState, useRef } from "react";
import { HomeOutlined, UserOutlined, SearchOutlined } from "@ant-design/icons";
import { Breadcrumb, Button, Input, notification, Space, Table, Tag, Switch } from "antd";
import type { InputRef, TableColumnsType, TableColumnType } from 'antd';
import type { FilterDropdownProps } from "antd/es/table/interface";
import Highlighter from "react-highlight-words";
import axios from 'axios';
import "./data_picking_return.css";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import useSWR from 'swr';
const fetcher = (url: string) => axios.get(url).then(res => res.data)


interface PickingReturnData {
    id: number;
    picking_list_return_no: string;
    picking_list_return_date: string;
    picking_list_no: string;
    sales_order_no: string;
    customer_name: string;
    status: string;
    warehouse: string;
}

type DataIndex = keyof PickingReturnData;

const DataPickingReturn: React.FC = () => {
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };
    const [isProgress, setIsProgress] = useState(false);
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const { userData } = useAuth()
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);
    const navigate = useNavigate();
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [filters, setFilters] = useState<any>({});
    const [sorter, setSorter] = useState<any>({});
    const [showAllWarehouse, setShowAllWarehouse] = useState(false);
    const otoritasList = userData?.otoritas ? userData.otoritas.split(';') : [];
    const hasOtoritas77 = otoritasList.includes('77');

    const buildUrl = () => {
        const params = new URLSearchParams({
            page: pagination.current.toString(),
            limit: pagination.pageSize.toString(),
            warehouse: showAllWarehouse ? '' : (userData?.warehouse_code || ''),
        });
        Object.keys(filters).forEach(key => {
            if (filters[key] && filters[key][0] !== undefined) params.append(key, filters[key][0]);
        });
        if (sorter.field && sorter.order) {
            params.append('sortField', sorter.field);
            params.append('sortOrder', sorter.order === 'ascend' ? 'ASC' : 'DESC');
        }
        return `${backendUrl}/api/picking-list-returns?${params.toString()}`;
    };

    const { data: swrData, isLoading, mutate } = useSWR((userData?.warehouse_code || showAllWarehouse) ? buildUrl() : null, fetcher, { refreshInterval: 5000 });

    const handleTableChange = (
        newPagination: any,
        tableFilters: any,
        tableSorter: any
    ) => {
        setPagination(prev => ({ ...prev, current: newPagination.current, pageSize: newPagination.pageSize }));
        setFilters(tableFilters);
        setSorter(tableSorter);
    };

    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps['confirm'],
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<PickingReturnData> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex.toString().replace(/_/g, ' ')}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        Filter
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) =>
            record[dataIndex]
                ? record[dataIndex].toString().toLowerCase().includes((value as string).toLowerCase())
                : false,
        filterDropdownProps: {
            onOpenChange(open) {
                if (open) {
                    setTimeout(() => searchInput.current?.select(), 100);
                }
            },
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });

    const columns: TableColumnsType<PickingReturnData> = [
        {
            title: 'Picking List Return No',
            dataIndex: 'picking_list_return_no',
            key: 'picking_list_return_no',
            ...getColumnSearchProps('picking_list_return_no'),
            sorter: true,
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Picking List Return Date',
            dataIndex: 'picking_list_return_date',
            key: 'picking_list_return_date',
            ...getColumnSearchProps('picking_list_return_date'),
            sorter: true,
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Picking List No.',
            dataIndex: 'picking_list_no',
            key: 'picking_list_no',
            ...getColumnSearchProps('picking_list_no'),
            sorter: true,
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Sales Order No.',
            dataIndex: 'sales_order_no',
            key: 'sales_order_no',
            ...getColumnSearchProps('sales_order_no'),
            sorter: true,
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Customer Name',
            dataIndex: 'customer_name',
            key: 'customer_name',
            ...getColumnSearchProps('customer_name'),
            sorter: true,
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            ...getColumnSearchProps('status'),
            sorter: true,
            sortDirections: ['descend', 'ascend'],
            render: (status: string | null) => {
                let text = status;

                if (status === '1') {
                    text = 'Draft';
                } else if (status === '4') {
                    text = 'Close / Complete';
                } else if (!status) {
                    text = 'N/A';
                }

                const renderedText = text ? (text === 'Close / Complete' ? text : text.toUpperCase()) : '';

                return searchedColumn === 'status' ? (
                    <Highlighter
                        highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                        searchWords={[searchText]}
                        autoEscape
                        textToHighlight={renderedText}
                    />
                ) : (
                    renderedText
                );
            },
        },
    ];

    return (

        <div id="ilmf" style={transformStyle}>
            <div className="gjs-grid-row" id="i5mj">
                <div className="gjs-grid-column" id="ijbw">
                    <Breadcrumb
                        items={[
                            {
                                title: <HomeOutlined />,
                            },
                            {
                                href: "#/dashboard/data_picking_return",
                                title: (
                                    <>
                                        <span>Data Picking Return</span>
                                    </>
                                ),
                            },
                            {
                                href: "#/dashboard/picking_list_return",
                                title: "Picking List Return",
                            },
                        ]}
                    />
                </div>
            </div>


            <div className="gjs-grid-row" id="uuhfk">
                <div className="gjs-grid-column" id="yyrhd">
                    {hasOtoritas77 && (
                        <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Switch checked={showAllWarehouse} onChange={setShowAllWarehouse} />
                            <span style={{ fontSize: '14px' }}>Show All Warehouse</span>
                        </div>
                    )}
                    <Table
                        columns={columns}
                        dataSource={swrData?.data || []}
                        rowKey="id"
                        loading={isLoading || isProgress}
                        bordered
                        pagination={{ ...pagination, total: parseInt(swrData?.total, 10) || 0 }}
                        onChange={handleTableChange}
                        onRow={(record) => ({
                            onDoubleClick: () => {
                                if (isProgress) return;
                                setIsProgress(true);
                                navigate('/dashboard/picking_list_return', {
                                    state: {
                                        pickingListReturnNo: record.picking_list_return_no,
                                        status: record.status,
                                    },
                                });
                            },
                        })}
                    />
                </div>
            </div>
        </div>
    );
};

export default DataPickingReturn;